/* eslint-disable eqeqeq */
import { inject, Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { StatefulService } from '@app/service/stateful.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoOnChangeFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly flowHelper: OaoPageHelper
  ) {}

  /** Method to remove first cChar zero(0) to percentage field  */
  public removeFirstCharZero = (context: LogicFnContext): void => {
    if (context.field && context.field.data.value && context.field.data.value.toString().charAt(0) === '0') {
      context.field.data.value =
        parseFloat(context.field.data.value.toString()) == 0 ? `%` : parseFloat(context.field.data.value.toString()) + `%`;
    }
  };

  /**
   * Load BeneficialOwner && authorizedSigners list for business
   *   * @param arrayPath - Path of respective business array
   */
  /** To load Additional Reponsible and Authorized card List */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public updateBSAApplicantDetails = (context: LogicFnContext): void => {
    const ResponsiblePartyCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
    ).valueStringOnly;
    if (ResponsiblePartyCount && Number.parseInt(ResponsiblePartyCount) > 0) {
      const lstCount = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      for (let i = 0; i < lstCount; i++) {
        const percentage = this.statefulService.wrap(
          `api:oaoMemberInformation/businessInformation/responsibleParties/${i}/percentage`
        ).value;
        if (percentage) {
          const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/responsibleParties/${i}`);
          this.statefulService.wrap(
            // eslint-disable-next-line max-len
            `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/responsibleParties/${i}/applicantName`
          ).value = objApplicant;
        }
      }
    }

    const BeneficialOwnerCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
    ).valueStringOnly;
    if (BeneficialOwnerCount && Number.parseInt(BeneficialOwnerCount) > 0) {
      const lstCount = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/additionalBeneficialOwner');
      for (let i = 0; i < lstCount; i++) {
        const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/additionalBeneficialOwner/${i}`);
        this.statefulService.wrap(
          // eslint-disable-next-line max-len
          `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/beneficialOwner/${i}/applicantName`
        ).value = objApplicant;
      }
    }

    const additionalAuthorizedCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
    ).valueStringOnly;
    if (additionalAuthorizedCount && Number.parseInt(additionalAuthorizedCount) > 0) {
      const lstCount = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners');
      for (let i = 0; i < lstCount; i++) {
        const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners/${i}`);
        this.statefulService.wrap(
          // eslint-disable-next-line max-len
          `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/authorizedSigners/${i}/applicantName`
        ).value = objApplicant;
      }
    }
  };

  /** Method to get appliant full name  */
  public getOAOApplicantName(apiPrimaryRoot: string): string {
    return (
      this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueString +
      ' ' +
      this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueString
    );
  }

  public submitFinalCompareAndFraud = async (context: LogicFnContext, path: string, edit: string): Promise<void> => {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;

    const executeCompareAndFraud = async (): Promise<void> => {
      await this.flowHelper.runCompareAndFraudVerificationAsync(path, context.pageIteration);
      this.statefulService.wrap(edit, undefined, context.pageIteration).value = false; // Reset the edit state
      context.defaultNavigationFn();
    };

    if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
      await executeCompareAndFraud();
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/applicantId`).load(executeCompareAndFraud);
    }
  };

  /**
   * Method to remove OAO co-applicant and continue
   * This is used in the OAO flow to remove a co-applicant when the user chooses to do so.
   * It will remove the co-applicant and continue with the workflow.
   */
  public removeOaoCoApplicant = (): void => {
    this.flowHelper.removeOaoApplicantAndContinue();
  };
}
